import { useState } from 'react'
import styled from 'styled-components'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import KanbanCard from './KanbanCard'
import ModalCard from '../modal/ModalCard'
import useTranslation from '../../hooks/useTranslation'
import { Icon_Plus } from '../../styles/icons'

const Column = styled.div`
    display: flex;
    flex-direction: column;
    width: 280px;
    min-width: 120px;
    flex-shrink: 1;
    background: ${({ theme }) => theme.card.background};
    border: 1px solid ${({ theme }) => theme.area.lightBorder};
    border-radius: 0.6rem;
    overflow: hidden;
`

const ColumnHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 0.9rem 0.6rem;
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
`

const ColumnTitle = styled.span`
    font-size: var(--font-S);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    flex: 1;

    @media (max-width: ${({ theme }) => theme.breakpoint.s}) {
        font-size: var(--font-XS);
    }
`

const CountBadge = styled.span`
    font-size: var(--font-XS);
    background: ${({ theme }) => theme.area.highBg};
    color: ${({ theme }) => theme.font.label};
    border-radius: 0.75rem;
    padding: 0.1rem 0.5rem;
    font-weight: 500;
`

const AddButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
    padding: 0.2rem;
    border-radius: 0.25rem;
    font-size: 1rem;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.font.color};
        background: ${({ theme }) => theme.form.panelBg};
    }

    @media (max-width: ${({ theme }) => theme.breakpoint.l}) {
        display: none;
    }
`

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 0.6rem;
    min-height: 80px;
    flex: 1;
`

const KanbanSwimlane = ({ swimlane, tasks, parentId, parentEntity, parentPath, subEntity, cardConfig, defaultPriority, onTaskCreated, nextStackRank }) => {
    const { t } = useTranslation()
    const [modalOpen, setModalOpen] = useState(false)

    const { setNodeRef } = useDroppable({
        id: swimlane.id,
        data: { swimlaneId: swimlane.id }
    })

    const sortableIds = tasks.map(t => t.id)

    const handleAddClick = (e) => {
        e.stopPropagation()
        setModalOpen(true)
    }

    const handleModalClose = () => {
        setModalOpen(false)
        onTaskCreated()
    }

    return (
        <Column>
            <ColumnHeader>
                <ColumnTitle>{t(swimlane.title)}</ColumnTitle>
                <CountBadge>{tasks.length}</CountBadge>
                <AddButton onClick={handleAddClick} title={t('kanban.swimlane.add.task')}
                    data-testid={`kanban.${subEntity}.${swimlane.statusValue}.add`}>
                    {Icon_Plus}
                </AddButton>
            </ColumnHeader>

            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <CardList ref={setNodeRef}>
                    {tasks.map((task, idx) => (
                        <KanbanCard
                            key={task.id}
                            task={task}
                            testId={`kanban.${subEntity}.${swimlane.statusValue}.${idx + 1}`}
                            swimlaneId={swimlane.id}
                            parentId={parentId}
                            parentEntity={parentEntity}
                            parentPath={parentPath}
                            subEntity={subEntity}
                            cardConfig={cardConfig}
                        />
                    ))}
                </CardList>
            </SortableContext>

            {modalOpen && (
                <ModalCard
                    isVisible={true}
                    setIsVisible={setModalOpen}
                    entity={subEntity}
                    origin={{ id: parentId }}
                    mode="new"
                    testId={`kanban.${subEntity}.modal`}
                    load={false}
                    onClose={handleModalClose}
                    effect={handleModalClose}
                    defaultValues={{
                        status:     swimlane.statusValue,
                        statusName: t(swimlane.title),
                        priority:   defaultPriority,
                        stackRank:  nextStackRank
                    }}
                />
            )}
        </Column>
    )
}

export default KanbanSwimlane
